import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { F, C, btn, inp } from '../config/styles';
import { makeCollection, currentMonthLabel } from '../utils/products';
import { fetchProfile, ensureProfile, deleteCollection as deleteCollectionFromDB } from '../utils/storage';
import { useAppData } from '../hooks/useAppData';
import { TenantContext, useTenant } from '../hooks/useTenant';
import { ProductEditor } from '../components/products/ProductEditor';
import { CalendarView } from '../components/views/CalendarView';
import { Dashboard } from '../components/views/Dashboard';
import { SpringboardView } from '../components/views/SpringboardView';
import { TeamView } from '../components/views/TeamView';
import { LoginScreen } from '../components/auth/LoginScreen';
import { SpreadsheetView } from '../components/views/SpreadsheetView';
import { PriceTableView } from '../components/views/PriceTableView';
import { SizeTableView } from '../components/views/SizeTableView';
import { ExportView } from '../components/views/ExportView';
import { TenantAdminView } from '../components/views/TenantAdminView';
import { WorkspaceSettingsView } from '../components/views/WorkspaceSettingsView';
import { AdminPortal } from '../components/auth/AdminPortal';
import { StudioProductsView } from '../components/views/StudioProductsView';
import { CropJobsTray } from '../components/ui/CropJobsTray';
import { setMediaPatcher } from '../utils/cropJobs';

export default function App() {
  const [user, setUser]           = useState(null);
  const [readyUserId, setReadyUserId] = useState(null); // set only after profile has tenant_id
  const [authLoading, setAuthLoading] = useState(true);
  const [roles, setRoles]         = useState(null);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [profileTenantId, setProfileTenantId] = useState(null); // the user's own tenant (from their profile)

  // UI-only state
  const [activeTab, setActiveTab]           = useState('products');
  const [newCollName, setNewCollName]       = useState('');
  const [newCollMonth, setNewCollMonth]     = useState('');
  const [addingColl, setAddingColl]         = useState(false);
  const [editingMonthIdx, setEditingMonthIdx] = useState(null);
  const [editingNameIdx, setEditingNameIdx]   = useState(null);
  const [expandedMonths, setExpandedMonths]   = useState(new Set());

  const editingProductOriginal = useRef(null);

  const { tenantValue, tenantLoading, tenantError } = useTenant();

  const profileToRoles = (p) => {
    if (!p) return [];
    if (Array.isArray(p.roles) && p.roles.length > 0) return p.roles;
    if (p.role && p.role !== 'pending') return [p.role];
    return [];
  };
  const applyProfile = (p) => {
    setRoles(profileToRoles(p));
    setIsPlatformAdmin(p?.is_platform_admin === true);
    setProfileTenantId(p?.tenant_id ?? null);
  };

  const {
    collections, activeCollIdx, activeCollection,
    priceTable, sizeTable, launchCalendar, saveStatus,
    editingProduct, setEditingProduct,
    updateCollections, updateProducts, updateProduct, patchProduct,
    updatePriceTable, updateSizeTable, updateCalendar,
    switchCollection,
  } = useAppData(readyUserId, tenantValue?.tenantId);

  // Background video crops (utils/cropJobs) swap their results in through this.
  useEffect(() => { setMediaPatcher(patchProduct); });

  // Inject fonts once
  useEffect(() => {
    const fonts = [
      ['lounge-font-inter', 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap'],
      ['lounge-font-jb',    'https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500&display=swap'],
    ];
    fonts.forEach(([id, href]) => {
      if (!document.getElementById(id)) {
        const l = document.createElement('link');
        l.id = id; l.rel = 'stylesheet'; l.href = href;
        document.head.appendChild(l);
      }
    });
  }, []);

  // Guard against the browser's default drag-and-drop behaviour: dragging any
  // <img> (product thumbnails, CAD images, etc.) and dropping it outside a
  // component that explicitly handles the drop navigates the whole tab to the
  // image URL, which reloads the app back to its default view. Components that
  // want real drop behaviour (image reorder, folder upload) already call
  // preventDefault() themselves, so this only catches drops nothing else handled.
  useEffect(() => {
    const preventDefault = e => e.preventDefault();
    window.addEventListener('dragover', preventDefault);
    window.addEventListener('drop', preventDefault);
    return () => {
      window.removeEventListener('dragover', preventDefault);
      window.removeEventListener('drop', preventDefault);
    };
  }, []);

  // Auth — role always read from profiles table (single source of truth)
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) fetchProfile(session.user.id).then(p => {
        applyProfile(p);
        // Existing users whose profile already has tenant_id are ready to load data immediately.
        // New/backfilled users wait for ensureProfile (below) to set tenant_id first.
        if (p?.tenant_id) setReadyUserId(session.user.id);
      });
      setAuthLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) fetchProfile(session.user.id).then(p => {
        applyProfile(p);
        if (p?.tenant_id) setReadyUserId(session.user.id);
      });
      else { setRoles(null); setIsPlatformAdmin(false); setReadyUserId(null); }
    });
    return () => subscription.unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Create/backfill profile once both auth and tenant are resolved.
  // Handles: new users on any tenant, existing users missing tenant_id.
  // Always sets readyUserId after completion — this is the gate for storeLoad.
  useEffect(() => {
    if (!user?.id || !tenantValue?.tenantId) return;
    ensureProfile(user.id, user.email, tenantValue.tenantId).then(p => {
      applyProfile(p);
      setReadyUserId(user.id);
    });
  }, [user?.id, tenantValue?.tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Real-time role updates
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel('profile-role-changes')
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'profiles', filter: `id=eq.${user.id}` },
        (payload) => {
          const p = payload.new;
          if (!p) return;
          let updated = [];
          if (Array.isArray(p.roles) && p.roles.length > 0) updated = p.roles;
          else if (p.role && p.role !== 'pending') updated = [p.role];
          setRoles(updated);
          supabase.auth.refreshSession();
        })
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Collection management (UI-level wrappers around updateCollections) ──

  const addCollection = () => {
    if (!newCollName.trim()) return;
    const month    = newCollMonth.trim() || currentMonthLabel();
    const newColls = [...collections, makeCollection(newCollName.trim(), month)];
    updateCollections(newColls, newColls.length - 1);
    setNewCollName(''); setNewCollMonth(''); setAddingColl(false);
  };

  const updateCollectionMonth = (idx, month) => {
    updateCollections(collections.map((c, i) => i === idx ? { ...c, month } : c), activeCollIdx);
    setEditingMonthIdx(null);
  };

  const updateCollectionName = (idx, name) => {
    if (!name.trim()) { setEditingNameIdx(null); return; }
    updateCollections(collections.map((c, i) => i === idx ? { ...c, name: name.trim() } : c), activeCollIdx);
    setEditingNameIdx(null);
  };

  const deleteCollection = (idx) => {
    if (collections.length <= 1) { alert('You cannot delete the last collection.'); return; }
    const c = collections[idx];
    if (!window.confirm(`Delete collection "${c.name}" and all ${c.products.length} product${c.products.length !== 1 ? 's' : ''} in it? This cannot be undone.`)) return;
    deleteCollectionFromDB(c.id);
    const newColls = collections.filter((_, i) => i !== idx);
    updateCollections(newColls, Math.min(idx, newColls.length - 1));
  };

  const toggleMonthCollapse = (month) => {
    setExpandedMonths(prev => {
      const next = new Set(prev);
      next.has(month) ? next.delete(month) : next.add(month);
      return next;
    });
  };

  const monthOptions = () => {
    const now = new Date();
    return Array.from({ length: 22 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() + i - 3, 1);
      return d.toLocaleString('en-GB', { month: 'long', year: 'numeric' });
    });
  };

  const isManager = roles?.includes('manager') ?? false;

  if (tenantValue?.isAdminPortal) return <AdminPortal />;

  if (tenantLoading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: F.body, color: C.muted }}>
      Loading…
    </div>
  );

  if (tenantError === 'not_found') return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: F.body }}>
      <div style={{ textAlign: 'center', maxWidth: 380 }}>
        <div style={{ fontFamily: F.display, fontSize: 24, color: C.text, marginBottom: 12 }}>Range Builder</div>
        <div style={{ fontSize: 15, color: C.text, marginBottom: 8 }}>Workspace not found</div>
        <div style={{ fontSize: 13, color: C.muted }}>This URL doesn't match any configured workspace. Check the address and try again.</div>
      </div>
    </div>
  );

  if (tenantError === 'unavailable') return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: F.body }}>
      <div style={{ textAlign: 'center', maxWidth: 380 }}>
        <div style={{ fontFamily: F.display, fontSize: 24, color: C.text, marginBottom: 12 }}>Range Builder</div>
        <div style={{ fontSize: 15, color: C.text, marginBottom: 8 }}>Service unavailable</div>
        <div style={{ fontSize: 13, color: C.muted }}>Unable to reach the server. Check your connection and refresh.</div>
      </div>
    </div>
  );

  if (authLoading) return (
    <TenantContext.Provider value={tenantValue}>
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: F.body, color: C.muted }}>
        Loading…
      </div>
    </TenantContext.Provider>
  );
  if (!user) return (
    <TenantContext.Provider value={tenantValue}>
      <LoginScreen tenant={tenantValue} />
    </TenantContext.Provider>
  );

  // A signed-in user whose home tenant isn't this subdomain has no access to this
  // workspace. RLS already returns them an empty workspace at the database level;
  // this makes it explicit instead of showing a blank, editable shell that looks
  // like data loss. Platform admins bypass — they can view any workspace.
  const wrongWorkspace = !!(profileTenantId && tenantValue?.tenantId
    && profileTenantId !== tenantValue.tenantId && !isPlatformAdmin);
  if (wrongWorkspace) return (
    <TenantContext.Provider value={tenantValue}>
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: F.body }}>
        <div style={{ textAlign: 'center', maxWidth: 400, padding: 24 }}>
          <div style={{ fontFamily: F.display, fontSize: 24, color: C.text, marginBottom: 12 }}>
            {tenantValue?.branding?.appName || tenantValue?.tenantName || 'Range Builder'}
          </div>
          <div style={{ fontSize: 15, color: C.text, marginBottom: 8 }}>You don't have access to this workspace</div>
          <div style={{ fontSize: 13, color: C.muted, marginBottom: 24, lineHeight: 1.5 }}>
            Your account isn't a member of the <strong>{tenantValue?.tenantName || 'this'}</strong> workspace.
            Sign in with an account that belongs to it, or return to your own workspace.
          </div>
          <button onClick={() => supabase.auth.signOut()}
            style={{ ...btn('secondary', { fontSize: 13, padding: '8px 16px' }) }}>Sign out</button>
        </div>
      </div>
    </TenantContext.Provider>
  );

  if (!roles || roles.length === 0) return (
    <TenantContext.Provider value={tenantValue}>
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: F.body }}>
        <div style={{ textAlign: 'center', maxWidth: 380 }}>
          <div style={{ fontFamily: F.display, fontSize: 24, color: C.text, marginBottom: 12 }}>Range Builder</div>
          <div style={{ fontSize: 15, color: C.text, marginBottom: 8 }}>Account pending approval</div>
          <div style={{ fontSize: 13, color: C.muted, marginBottom: 24 }}>Your account has been created but hasn't been assigned a role yet. Please ask your manager to assign you a role in the Team tab.</div>
          <button onClick={() => supabase.auth.signOut()} style={{ fontSize: 13, color: C.muted, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Sign out</button>
        </div>
      </div>
    </TenantContext.Provider>
  );

  // ── TOP BAR ─────────────────────────────────────────────────
  const renderTopBar = (showCollTabs = true) => {
    const opts = monthOptions();

    const collLaunchDate = (coll) => {
      if (!launchCalendar.length) return null;
      const norm     = s => s.toLowerCase().replace(/[^a-z0-9 ]/g, ' ');
      const collWords = norm(coll.name).split(/\s+/).filter(Boolean);
      const collSet  = new Set(collWords);
      const hit = launchCalendar
        .filter(e => e.date && e.name)
        .filter(e => {
          const evWords = norm(e.name).split(/\s+/).filter(Boolean);
          return collWords.length > 0 && evWords.filter(w => collSet.has(w)).length / collWords.length >= 0.75;
        })
        .sort((a, b) => a.date.localeCompare(b.date))[0];
      return hit?.date || null;
    };

    const groupMap   = {};
    const groupOrder = [];
    collections.forEach((c, i) => {
      const m = c.month || 'Unscheduled';
      if (!groupMap[m]) { groupMap[m] = []; groupOrder.push(m); }
      groupMap[m].push({ c, i, launchDate: collLaunchDate(c) });
    });
    const parseMonth = (m) => { if (m === 'Unscheduled') return Infinity; const d = new Date(m); return isNaN(d) ? Infinity : d.getTime(); };
    groupOrder.sort((a, b) => parseMonth(a) - parseMonth(b));
    Object.values(groupMap).forEach(group => group.sort((a, b) => {
      if (!a.launchDate && !b.launchDate) return 0;
      if (!a.launchDate) return 1; if (!b.launchDate) return -1;
      return a.launchDate.localeCompare(b.launchDate);
    }));

    return (
      <div style={{ height: 52, background: C.white, borderBottom: `1px solid ${C.e8}`, display: 'flex', alignItems: 'center', padding: '0 20px', position: 'sticky', top: 0, zIndex: 20, gap: 0 }}>
        {tenantValue?.branding?.logoUrl ? (
          <div style={{ height: 18, maxWidth: 180, marginRight: 20, flexShrink: 0, overflow: 'hidden', display: 'flex', alignItems: 'center' }}>
            <img src={tenantValue.branding.logoUrl} alt={tenantValue.tenantName} style={{ height: 18, width: 'auto', maxWidth: 180, display: 'block' }} />
          </div>
        ) : (
          <span style={{ fontFamily: F.display, fontSize: 19, color: C.text, marginRight: 20, whiteSpace: 'nowrap', flexShrink: 0 }}>
            {tenantValue?.branding?.appName || tenantValue?.tenantName || 'Range Builder'}
          </span>
        )}

        {showCollTabs && (
          <div style={{ display: 'flex', alignItems: 'stretch', overflowX: 'auto', flex: 1, gap: 0 }}>
            {groupOrder.map(month => {
              const collapsed = !expandedMonths.has(month);
              return (
                <div key={month} style={{ display: 'flex', alignItems: 'stretch', borderRight: `1px solid ${C.e8}`, marginRight: 4, paddingRight: 4 }}>
                  <button onClick={() => toggleMonthCollapse(month)} title={collapsed ? 'Expand' : 'Collapse'}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3, padding: '0 8px 0 4px', fontSize: 10, fontWeight: 600, color: C.ph, letterSpacing: '0.06em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                    <span style={{ fontSize: 8, opacity: 0.6 }}>{collapsed ? '▶' : '▼'}</span>
                    {month}
                  </button>
                  {!collapsed && groupMap[month].map(({ c, i }) => (
                    <div key={c.id} style={{ display: 'flex', alignItems: 'center' }}>
                      {editingNameIdx === i ? (
                        <input autoFocus style={{ ...inp({ padding: '3px 6px', fontSize: 12, width: 140 }), margin: '0 4px' }}
                          defaultValue={c.name}
                          onBlur={e => updateCollectionName(i, e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') updateCollectionName(i, e.target.value); if (e.key === 'Escape') setEditingNameIdx(null); }} />
                      ) : editingMonthIdx === i ? (
                        <select autoFocus style={{ ...inp({ padding: '3px 6px', fontSize: 11, width: 140 }), margin: '0 4px' }}
                          defaultValue={c.month || currentMonthLabel()}
                          onChange={e => updateCollectionMonth(i, e.target.value)}
                          onBlur={() => setEditingMonthIdx(null)}>
                          {opts.map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                      ) : (
                        <button onClick={() => { switchCollection(i); setActiveTab('products'); }}
                          onDoubleClick={() => isManager && setEditingNameIdx(i)}
                          title={isManager ? 'Double-click to rename' : undefined}
                          style={{ ...btn('ghost', { padding: '0 12px', fontSize: 13, borderRadius: 0, border: 'none', borderBottom: `2px solid ${i === activeCollIdx ? C.brand : 'transparent'}`, height: 52 }), fontWeight: i === activeCollIdx ? 600 : 400, color: i === activeCollIdx ? C.text : C.muted, whiteSpace: 'nowrap' }}>
                          {c.name}
                        </button>
                      )}
                      {isManager && i === activeCollIdx && editingNameIdx !== i && editingMonthIdx !== i && (
                        <button onClick={e => { e.stopPropagation(); setEditingMonthIdx(i); }} title="Change month"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.ph, fontSize: 10, padding: '0 2px', lineHeight: 1, flexShrink: 0 }}>✎</button>
                      )}
                      {isManager && i === activeCollIdx && collections.length > 1 && editingNameIdx !== i && editingMonthIdx !== i && (
                        <button onClick={e => { e.stopPropagation(); deleteCollection(i); }} title="Delete collection"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.ph, fontSize: 14, padding: '0 6px 0 0', lineHeight: 1, flexShrink: 0 }}>×</button>
                      )}
                    </div>
                  ))}
                </div>
              );
            })}
            {isManager && (
              addingColl ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '0 10px' }}>
                  <input style={{ ...inp({ padding: '5px 8px', fontSize: 12, width: 140 }) }}
                    value={newCollName} onChange={e => setNewCollName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') addCollection(); if (e.key === 'Escape') { setAddingColl(false); setNewCollName(''); setNewCollMonth(''); } }}
                    placeholder="Collection name…" autoFocus />
                  <select style={{ ...inp({ padding: '5px 8px', fontSize: 12 }) }}
                    value={newCollMonth || currentMonthLabel()} onChange={e => setNewCollMonth(e.target.value)}>
                    {opts.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                  <button style={btn('primary', { padding: '5px 10px', fontSize: 11 })} onClick={addCollection}>Add</button>
                  <button style={btn('ghost', { padding: '5px 6px', fontSize: 16, color: C.muted })} onClick={() => { setAddingColl(false); setNewCollName(''); setNewCollMonth(''); }}>×</button>
                </div>
              ) : (
                <button style={{ ...btn('ghost', { padding: '0 12px', fontSize: 20, borderRadius: 0, height: 52, color: C.muted, border: 'none' }) }}
                  onClick={() => setAddingColl(true)} title="New collection">+</button>
              )
            )}
          </div>
        )}

        {!showCollTabs && <div style={{ flex: 1 }} />}

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0, marginLeft: 16 }}>
          <span style={{ fontSize: 11, color: C.ph, fontFamily: F.body }}>{saveStatus === 'saving' ? 'Saving…' : 'Saved'}</span>
          <span style={{ fontSize: 12, color: C.text2, fontFamily: F.body }}>
            {roles?.filter(r => r !== 'manager').map(r => tenantValue.roles.find(x => x.id === r)?.label).filter(Boolean).join(', ') || (isManager ? 'Build Manager' : '')}
          </span>
          <button style={{ ...btn('ghost', { fontSize: 12, padding: '5px 10px', border: `1px solid ${C.e8}`, borderRadius: 6, color: C.muted }) }}
            onClick={() => supabase.auth.signOut()}>Sign out</button>
        </div>
      </div>
    );
  };

  // ── PRODUCT EDITOR VIEW ─────────────────────────────────────
  if (editingProduct) {
    const orig    = editingProductOriginal.current;
    const isDirty = orig && JSON.stringify(orig) !== JSON.stringify(editingProduct);
    const collectionProducts = activeCollection?.products || [];
    const currentIdx  = collectionProducts.findIndex(p => p.id === editingProduct.id);
    const navigateTo  = (p) => { editingProductOriginal.current = p; setEditingProduct(p); };
    const prevProduct = currentIdx > 0 ? collectionProducts[currentIdx - 1] : null;
    const nextProduct = currentIdx < collectionProducts.length - 1 ? collectionProducts[currentIdx + 1] : null;
    return (
      <TenantContext.Provider value={tenantValue}>
      <div style={{ minHeight: '100vh', background: C.white }}>
        <CropJobsTray />
        {renderTopBar(false)}
        {isDirty && (
          <div style={{ background: '#fef3c7', borderBottom: '1px solid #fcd34d', padding: '8px 24px', display: 'flex', alignItems: 'center', gap: 12, fontSize: 13 }}>
            <span style={{ color: '#92400e', flex: 1 }}>You have unsaved changes to this product.</span>
            <button style={{ ...btn('secondary', { fontSize: 12, padding: '5px 12px' }), color: '#92400e', borderColor: '#fcd34d', background: '#fef3c7' }}
              onClick={() => updateProduct(orig)}>Revert changes</button>
          </div>
        )}
        <ProductEditor product={editingProduct} roles={roles} onUpdate={updateProduct} collectionName={activeCollection?.name || ''}
          onBack={() => { editingProductOriginal.current = null; setEditingProduct(null); }} priceTable={priceTable}
          onPrev={prevProduct ? () => navigateTo(prevProduct) : null}
          onNext={nextProduct ? () => navigateTo(nextProduct) : null}
          productIndex={currentIdx} productCount={collectionProducts.length} />
      </div>
      </TenantContext.Provider>
    );
  }

  // ── MAIN VIEW ───────────────────────────────────────────────
  const TAB_LABELS = { products: 'Products', springboard: 'Springboard', spreadsheet: 'Spreadsheet', calendar: 'Calendar', prices: 'Price Table', sizes: 'Size Table', export: 'Export', team: 'Team', settings: 'Settings', tenants: 'Tenants' };
  const studioOnly = tenantValue?.studioOnly;
  const tabs = studioOnly
    ? ['products', ...(isManager ? ['team', 'settings'] : []), ...(isPlatformAdmin ? ['tenants'] : [])]
    : [
        'products', 'springboard', 'spreadsheet', 'calendar',
        ...((isManager || roles.includes('trading')) ? ['prices', 'sizes'] : []),
        ...(isManager ? ['export', 'team', 'settings'] : []),
        ...(isPlatformAdmin ? ['tenants'] : []),
      ];

  return (
    <TenantContext.Provider value={tenantValue}>
    <div style={{ minHeight: '100vh', background: C.white, fontFamily: F.body }}>
      <CropJobsTray />
      {renderTopBar()}

      <div style={{ display: 'flex', borderBottom: `1px solid ${C.e8}`, background: C.fa, padding: '0 20px' }}>
        {tabs.map(t => (
          <button key={t} onClick={() => setActiveTab(t)} style={{
            ...btn('ghost', { padding: '10px 16px', fontSize: 13, borderRadius: 0, border: 'none', borderBottom: `2px solid ${t === activeTab ? C.brand : 'transparent'}`, height: 42 }),
            fontWeight: t === activeTab ? 600 : 400, color: t === activeTab ? C.text : C.muted,
          }}>{TAB_LABELS[t]}</button>
        ))}
      </div>

      {activeTab === 'springboard' ? (
        <SpringboardView collection={activeCollection} roles={roles} onUpdateProducts={updateProducts} launchCalendar={launchCalendar} priceTable={priceTable} />
      ) : activeTab === 'calendar' ? (
        <CalendarView events={launchCalendar} onUpdate={updateCalendar} isManager={isManager}
          collections={collections}
          onOpenCollection={idx => { setActiveTab('products'); switchCollection(idx); }}
          onCreateCollection={(name, month) => {
            const newColl  = makeCollection(name, month);
            const newColls = [...collections, newColl];
            updateCollections(newColls, newColls.length - 1);
          }} />
      ) : activeTab === 'settings' && isManager ? (
        <WorkspaceSettingsView />
      ) : activeTab === 'tenants' && isPlatformAdmin ? (
        <TenantAdminView />
      ) : activeTab === 'team' && isManager ? (
        <TeamView currentUserId={user?.id} />
      ) : activeTab === 'export' && isManager ? (
        <ExportView products={activeCollection?.products || []} collectionId={activeCollection?.id}
          priceTable={priceTable} sizeTable={sizeTable} onUpdateProducts={updateProducts}
          currentUser={user} />
      ) : activeTab === 'prices' && (isManager || roles.includes('trading')) ? (
        <PriceTableView priceTable={priceTable} onUpdate={updatePriceTable} />
      ) : activeTab === 'sizes' && (isManager || roles.includes('trading')) ? (
        <SizeTableView sizeTable={sizeTable} onUpdate={updateSizeTable} />
      ) : activeTab === 'spreadsheet' ? (
        <SpreadsheetView collection={activeCollection} roles={roles} onUpdateProducts={updateProducts} />
      ) : studioOnly ? (
        <StudioProductsView collection={activeCollection} onUpdateProduct={updateProduct} onUpdateProducts={updateProducts} />
      ) : (
        <Dashboard collection={activeCollection} roles={roles}
          onEditProduct={p => { editingProductOriginal.current = p; setEditingProduct(p); }}
          onUpdateProducts={updateProducts}
          onDuplicate={newColl => updateCollections([...collections, newColl], collections.length)} />
      )}
    </div>
    </TenantContext.Provider>
  );
}
